import React from 'react';
import styled from 'styled-components';

import { Icon, Tooltip } from '@column/column-ui-kit';

export enum StateCellOption {
  Idle = 'idle',
  Loading = 'loading',
  Success = 'success',
  Error = 'error',
}

const IdleIcon = styled(Icon.CirclePendingTwo)`
  --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

const LoadingIcon = styled(Icon.Loading)`
  --icon-color: ${({ theme }) => theme.primary.background};
`;

const SuccessIcon = styled(Icon.CircleCheck)`
  --icon-color: ${({ theme }) => theme.success.background};
`;

const ErrorTooltip = styled(Tooltip)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ErrorIcon = styled(Icon.CircleCross)`
  --icon-color: ${({ theme }) => theme.danger.background};
`;

const State = styled.div<{ $state: StateCellOption }>`
  --icon-size: 20px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  width: 100%;
  height: 40px;
  position: relative;
  box-sizing: border-box;
  padding-left: 40px;
  display: flex;
  align-items: center;
  font-feature-settings: 'tnum';

  ${IdleIcon},
  ${LoadingIcon},
  ${SuccessIcon},
  ${ErrorTooltip} {
    position: absolute;
    top: 50%;
    left: 50%;
    margin: -10px 0 0 -10px;
    transition: opacity 0.3s ease;
  }

  ${IdleIcon} {
    opacity: ${({ $state }) => ($state === StateCellOption.Idle ? 1 : 0)};
    transition-delay: ${({ $state }) => ($state === StateCellOption.Idle ? '0.2s' : '0s')};
    pointer-events: ${({ $state }) => ($state === StateCellOption.Idle ? 'auto' : 'none')};
  }

  ${LoadingIcon} {
    opacity: ${({ $state }) => ($state === StateCellOption.Loading ? 1 : 0)};
    transition-delay: ${({ $state }) => ($state === StateCellOption.Loading ? '0.2s' : '0s')};
    pointer-events: ${({ $state }) => ($state === StateCellOption.Loading ? 'auto' : 'none')};
  }

  ${SuccessIcon} {
    opacity: ${({ $state }) => ($state === StateCellOption.Success ? 1 : 0)};
    transition-delay: ${({ $state }) => ($state === StateCellOption.Success ? '0.2s' : '0s')};
    pointer-events: ${({ $state }) => ($state === StateCellOption.Success ? 'auto' : 'none')};
  }

  ${ErrorTooltip} {
    opacity: ${({ $state }) => ($state === StateCellOption.Error ? 1 : 0)};
    transition-delay: ${({ $state }) => ($state === StateCellOption.Error ? '0.2s' : '0s')};
    pointer-events: ${({ $state }) => ($state === StateCellOption.Error ? 'auto' : 'none')};
  }
`;

export const StateCell = ({ message, state }: { message?: string; state: StateCellOption }) => (
  <State $state={state}>
    <IdleIcon />
    <LoadingIcon />
    <SuccessIcon />
    <ErrorTooltip content={message && state === StateCellOption.Error ? message : undefined}>
      <ErrorIcon />
    </ErrorTooltip>
  </State>
);
